import React from 'react';
import { useHistory } from 'react-router-dom';
import {
	Settings as SettingsIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Box, ClickAwayListener, Popper, MenuList, Paper } from '@material-ui/core';
import GoldButton from '@components/GoldButton/GoldButton';
import { useSelector } from 'react-redux';
import { UserProfilePrimaryRoleSelector } from '@store/selectors/UserProfileSelector';
import manageCaseOptionsByRole from '@components/CaseDetails/ManageCaseDropDown/utilities/manageCaseOptionsByRole';
import { CaseDetailsAppointmentSelector } from '@store/selectors/CaseDetailsSelector';
import { CaseDetailsSources, CaseDetailsPageState } from '@interfaces/CaseDetails/CaseDetailsPageState';
import { useSsmUiFlags } from '@utilities/hooks/useSsmUiFlags/useSsmUiFlags';
import {
	dropDownOptionsConfig
} from '@components/CaseDetails/ManageCaseDropDown/DropDownOptions/dropDownOptionsConfig';

const useStyles = makeStyles((theme) => ({
	menuButton: {
		'&.MuiButton-contained': {
			padding: theme.spacing(1, 2),
		},
	},
}));

export const ManageCaseDropDown: React.FC = () => {
	const history = useHistory<CaseDetailsPageState>();
	const classes = useStyles();
	const [isOpen, setIsOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const { wShowSecondarySurgeon } = useSsmUiFlags();
	const isFromSecondarySurgeon = history?.location?.state?.from === CaseDetailsSources.SdsAsSecondarySurgeon;

	const handleToggle = () => {
		setIsOpen((prevOpen) => !prevOpen);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	const { status } = useSelector(CaseDetailsAppointmentSelector);
	const role = useSelector(UserProfilePrimaryRoleSelector);

	const options = React.useMemo(() => {
		if (!role) {
			return null;
		}

		if (isFromSecondarySurgeon && wShowSecondarySurgeon) {
			return dropDownOptionsConfig.filter(o =>
				o.id === 'VIEW_ACTIVITY_DROPDOWN_OPTION' || o.id === 'REFRESH_ACTIVITY_DROPDOWN_OPTION'
			);
		}

		return manageCaseOptionsByRole(status)[role];
	}, [isFromSecondarySurgeon, role, status, wShowSecondarySurgeon]);

	if (!options?.length) {  return null; }

	return (
		<Box display="flex" alignItems="center">
			<ClickAwayListener onClickAway={handleClose}>
				<div ref={anchorRef}>
					<GoldButton
						data-field="manageCase-dropDown-toggleButton"
						className={classes.menuButton}
						onClick={handleToggle}
						startIcon={<SettingsIcon />}
						endIcon={<KeyboardArrowDownIcon />}
					>
						Manage Case
					</GoldButton>
					<Popper
						open={isOpen}
						anchorEl={anchorRef.current}
						data-field="manageCase-dropDown-menu"
					>
						<Paper>
							<MenuList aria-label="Manage case dropdown menu" style={{ minWidth: anchorRef.current?.offsetWidth }}>
								{options.map(({ renderAs: RenderAs, id }) => <RenderAs key={id} closeDropdown={handleClose}/>)}
							</MenuList>
						</Paper>
					</Popper>
				</div>
			</ClickAwayListener>
		</Box>
	);
};
